import React, { useCallback, useEffect, useState } from "react";
import { Megaphone, Loader2, Send, CalendarClock, Trash2 } from "lucide-react";
import api from "../../api";
import { useToast } from "../../hooks/use-toast";

const LANG_LABELS = [
  { code: "en", label: "English" }, { code: "es", label: "Español" }, { code: "fr", label: "Français" },
  { code: "pt", label: "Português" }, { code: "de", label: "Deutsch" },
];

// One message, one audience. It lands in their LytePress inbox and their mailbox — now or later.
export const AdminBroadcast = () => {
  const { toast } = useToast();
  const [segments, setSegments] = useState([]);
  const [segment, setSegment] = useState("");
  const [langs, setLangs] = useState([]);
  const [lang, setLang] = useState("");
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [sendAt, setSendAt] = useState("");
  const [queue, setQueue] = useState([]);
  const [busy, setBusy] = useState("");

  const load = useCallback(() => {
    api.get("/admin/broadcast/segments", { params: lang ? { lang } : {} }).then((r) => {
      setSegments(r.data.segments);
      setLangs(r.data.languages || []);
    }).catch(() => setSegments([]));
    api.get("/admin/broadcast/scheduled").then((r) => setQueue(r.data.scheduled || [])).catch(() => setQueue([]));
  }, [lang]);
  useEffect(() => { load(); }, [load, lang]);

  const picked = segments.find((s) => s.key === segment);

  const send = async () => {
    setBusy("send");
    try {
      const { data } = await api.post("/admin/broadcast", {
        segment, lang, subject: subject.trim(), message: body.trim(),
        sendAt: sendAt ? new Date(sendAt).toISOString() : "",
      });
      toast(data.scheduled
        ? { title: "Scheduled", description: `Goes out ${new Date(data.scheduled).toLocaleString()}.` }
        : { title: `Sent to ${data.recipients} account(s)`, description: `${data.emailed} email(s) delivered.` });
      setSubject(""); setBody(""); setSegment(""); setSendAt("");
      load();
    } catch (e) { toast({ title: e?.response?.data?.detail || "Could not send that" }); }
    finally { setBusy(""); }
  };

  const cancel = async (item) => {
    if (!window.confirm(`Cancel "${item.subject}"?`)) return;
    setBusy(`cancel-${item.id}`);
    try {
      await api.post(`/admin/broadcast/scheduled/${item.id}/cancel`);
      toast({ title: "Cancelled" });
      load();
    } catch (e) { toast({ title: e?.response?.data?.detail || "Could not cancel" }); }
    finally { setBusy(""); }
  };

  const tomorrowAt = (hour) => {
    const d = new Date();
    d.setDate(d.getDate() + 1);
    d.setHours(hour, 0, 0, 0);
    const pad = (n) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(hour)}:00`;
  };

  return (
    <div className="bg-card border border-border rounded-2xl p-5" data-testid="admin-broadcast">
      <h2 className="font-bold text-foreground flex items-center gap-2">
        <Megaphone size={18} className="text-[#16a34a]" /> Broadcast
      </h2>
      <p className="text-xs text-muted-foreground mt-0.5 mb-3">
        Goes to every inbox in the audience and their email. Say something worth an interruption.
      </p>

      <div className="space-y-3">
        <div className="flex flex-wrap items-center gap-2 mb-3" data-testid="broadcast-langs">
          <span className="text-xs font-semibold text-muted-foreground">Language</span>
          {[{ code: "", label: "Any" }, ...LANG_LABELS.filter((l) => (langs.find((x) => x.code === l.code)?.count || 0) > 0 || l.code === "en")].map((l) => (
            <button key={l.code || "any"} onClick={() => setLang(l.code)} data-testid={`broadcast-lang-${l.code || "any"}`}
              className={`text-xs font-bold rounded-full px-3 py-1.5 border transition-colors ${lang === l.code
                ? "bg-[#16a34a] border-[#16a34a] text-white"
                : "border-border text-muted-foreground hover:border-[#16a34a] hover:text-[#16a34a]"}`}>
              {l.label}{l.code ? ` (${langs.find((x) => x.code === l.code)?.count ?? 0})` : ""}
            </button>
          ))}
        </div>
        <div className="flex flex-wrap gap-2" data-testid="broadcast-segments">
          {segments.map((s) => (
            <button key={s.key} onClick={() => setSegment(s.key)} data-testid={`broadcast-segment-${s.key}`}
              className={`text-xs font-bold rounded-full px-3 py-1.5 border transition-colors ${segment === s.key
                ? "bg-[#0f2a1a] text-white border-[#0f2a1a]"
                : "bg-card text-foreground border-border hover:border-[#16a34a]"}`}>
              {s.label} · {s.count}
            </button>
          ))}
        </div>
        <input value={subject} onChange={(e) => setSubject(e.target.value)} data-testid="broadcast-subject"
          placeholder="Subject — e.g. Pro is open, and your waitlist spot is live"
          className="w-full bg-secondary rounded-xl px-3 py-2.5 text-sm outline-none" />
        <textarea value={body} onChange={(e) => setBody(e.target.value)} rows={5} data-testid="broadcast-body"
          placeholder="Write it like you're talking to one shop owner, not a mailing list."
          className="w-full bg-secondary rounded-xl px-3 py-2.5 text-sm outline-none resize-none" />

        <div className="border border-border rounded-xl p-3 space-y-2">
          <p className="text-xs font-bold text-foreground flex items-center gap-1.5">
            <CalendarClock size={13} className="text-[#16a34a]" /> Send later (optional)
          </p>
          <div className="flex flex-wrap items-center gap-2">
            <input type="datetime-local" value={sendAt} onChange={(e) => setSendAt(e.target.value)}
              data-testid="broadcast-send-at"
              className="bg-secondary rounded-xl px-3 py-2 text-sm text-foreground outline-none" />
            <button onClick={() => setSendAt(tomorrowAt(7))} data-testid="broadcast-preset-7"
              className="text-xs font-bold border border-border hover:border-[#16a34a] px-3 py-2 rounded-xl transition-colors">
              Tomorrow 7am
            </button>
            <button onClick={() => setSendAt(tomorrowAt(9))} data-testid="broadcast-preset-9"
              className="text-xs font-bold border border-border hover:border-[#16a34a] px-3 py-2 rounded-xl transition-colors">
              Tomorrow 9am
            </button>
            {sendAt && (
              <button onClick={() => setSendAt("")} data-testid="broadcast-send-now"
                className="text-xs font-bold border border-border hover:border-rose-500/50 px-3 py-2 rounded-xl transition-colors">
                Send immediately instead
              </button>
            )}
          </div>
          <p className="text-xs text-muted-foreground">
            Your own clock. Anything scheduled goes out within ten minutes of the time you pick.
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <button onClick={send} disabled={busy === "send" || !segment || subject.trim().length < 2 || body.trim().length < 2}
            data-testid="broadcast-send-btn"
            className="inline-flex items-center gap-1.5 text-sm font-bold bg-[#16a34a] hover:bg-[#15803d] disabled:opacity-60 text-white px-4 py-2.5 rounded-xl transition-colors">
            {busy === "send" ? <Loader2 className="animate-spin" size={14} /> : sendAt ? <CalendarClock size={14} /> : <Send size={14} />}
            {!picked ? "Pick an audience"
              : sendAt ? `Schedule for ${picked.count} ${picked.label.toLowerCase()}`
                : `Send to ${picked.count} ${picked.label.toLowerCase()}`}
          </button>
          {picked?.count === 0 && <p className="text-xs text-[#b8860b]">Nobody in that audience yet.</p>}
        </div>

        {queue.length > 0 && (          <div className="border border-border rounded-xl p-3" data-testid="broadcast-queue">
            <p className="text-xs font-bold text-foreground mb-2">Waiting to go out</p>
            <div className="space-y-1.5">
              {queue.map((q) => (
                <div key={q.id} className="flex flex-wrap items-center gap-2 text-xs" data-testid={`broadcast-queued-${q.id}`}>
                  <span className="font-semibold text-foreground">{new Date(q.sendAt).toLocaleString()}</span>
                  <span className="text-muted-foreground">{q.segmentLabel} · {q.subject}</span>
                  <button onClick={() => cancel(q)} disabled={busy === `cancel-${q.id}`}
                    data-testid={`broadcast-cancel-${q.id}`}
                    className="ml-auto inline-flex items-center gap-1.5 font-bold border border-border hover:border-rose-500/50 px-2.5 py-1.5 rounded-lg transition-colors disabled:opacity-60">
                    {busy === `cancel-${q.id}` ? <Loader2 className="animate-spin" size={12} /> : <Trash2 size={12} />} Cancel
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminBroadcast;
